import React from "react";
import PasswordIcon from "@mui/icons-material/Password";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";

import AppButton from "../../design-system/components/AppButton";
import AppChip from "../../design-system/components/AppChip";
import AppPersonBadge from "../../design-system/components/AppPersonBadge";
import AppTable from "../../design-system/components/AppTable";
import AppText from "../../design-system/components/AppText";
import AppTooltip from "../../design-system/components/AppTooltip";
import { IconAttachment, IconChevronRight } from "../../design-system/icons";
import { formatAccessLevel } from "../../utils/accessLevels";

const TABLE_SCROLLBAR_ALLOWANCE = 18;

const ActionButton = ({ label, icon, variant = "neutral", className, onClick }) => (
  <AppTooltip content={label}>
    <AppButton
      variant={variant}
      size="xs"
      iconOnly
      className={className}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      {icon}
    </AppButton>
  </AppTooltip>
);

const AccessLevelChip = ({ value }) => {
  const normalized = String(value ?? "");
  const label = formatAccessLevel(normalized);
  const isPrivileged = ["Super_Admin", "Admin", "company_admin"].includes(normalized);

  return (
    <AppChip
      variant={isPrivileged ? "primary" : "muted"}
      appearance="outlined"
      size="sm"
      leadingIcon={isPrivileged ? <SecurityOutlinedIcon /> : <PersonOutlineOutlinedIcon />}
    >
      {label}
    </AppChip>
  );
};

const fullName = (row) => [row.first_name, row.last_name].filter(Boolean).join(" ");

/*
  Der MA-Badge bekommt eine eigene schmale Spalte ganz links, statt im Vornamen
  mitzulaufen: sonst beginnt der Text jeder Zeile hinter einem Kreis und die
  Spalte "Vorname" fluchtet nicht mit ihrer Kopfzeile.

  64px ist die Breite, die AppTable auch fuer Auswahl- und Aktionsspalten
  ansetzt (CONTROL_COLUMN_MIN_TRACK_WIDTH) — der Badge steht damit exakt im
  gleichen Raster wie die Kontrollkaestchen daneben.
*/
const AVATAR_COLUMN_WIDTH = 64;

const avatarColumn = {
  key: "avatar",
  label: "",
  width: AVATAR_COLUMN_WIDTH,
  grow: 0,
  align: "center",
  sortable: false,
  render: (_value, row) => <AppPersonBadge name={fullName(row)} size="md" />,
};

const TeamMainGrid = ({
  users,
  columnVisibility,
  departmentsById,
  accessLevel,
  availableWidth,
  documentsEnabled,
  t,
  onEditUser,
  onAttachmentsOpen,
  onPasswordDialogOpen,
  selectedUserIds,
  onSelectedUserIdsChange,
  readOnly = false,
}) => {
  const canDeleteUsers = !readOnly && ["Super_Admin", "Admin", "company_admin"].includes(accessLevel);
  const leadingColumns = [
    canDeleteUsers && {
      key: "selected",
      label: "",
      controlKind: "selection",
      sortable: false,
    },
  ].filter(Boolean);

  const separateIdentityColumns = [
    columnVisibility.first_name && {
      key: "first_name",
      label: t("team.columns.first_name"),
      minWidth: 180,
      grow: 1,
      render: (value) => (
        <AppText variant="heading-4" tone="primary" truncate>
          {value || "-"}
        </AppText>
      ),
    },
    columnVisibility.last_name && {
      key: "last_name",
      label: t("team.columns.last_name"),
      minWidth: 150,
      grow: 1,
      render: (value) => (
        <AppText variant="heading-4" tone="primary" truncate>
          {value || "-"}
        </AppText>
      ),
    },
    columnVisibility.email && {
      key: "email",
      label: t("team.columns.email"),
      minWidth: 240,
      grow: 1.5,
    },
  ].filter(Boolean);

  const nameColumn = {
    key: "name",
    label: t("team.columns.name"),
    minWidth: 200,
    grow: 1.4,
    sortAccessor: (row) => `${row.first_name || ""} ${row.last_name || ""}`.trim(),
    render: (_value, row) => (
      <AppText variant="heading-4" tone="primary" truncate>
        {fullName(row) || "-"}
      </AppText>
    ),
  };

  const nameWithEmailColumn = {
    ...nameColumn,
    minWidth: 280,
    grow: 2,
    render: (_value, row) => (
      <div className="team-table__identity">
        <AppText variant="heading-4" tone="primary" truncate>
          {fullName(row) || "-"}
        </AppText>
        <AppText variant="body-2" tone="secondary" truncate>
          {row.email || "-"}
        </AppText>
      </div>
    ),
  };

  const detailColumns = [
    columnVisibility.departments && {
      key: "selected_departments",
      label: t("team.columns.departments"),
      // Die einzige Spalte, deren Inhalt mit der Firma waechst — sie bekommt den
      // Platz, den die drei Chip-Spalten rechts durch grow: 0 abgeben.
      minWidth: 240,
      grow: 2,
      sortAccessor: (row) => (row.selected_departments || []).join(" "),
      render: (departments, row) => {
        if (!Array.isArray(departments) || departments.length === 0) return "-";

        return (
          <span className="team-table__departments">
            {departments.map((department, index) => {
              const departmentId = row.selected_department_ids?.[index];
              const known = departmentsById[String(departmentId)];
              const shorthand = known?.shorthand || department;

              // Ohne hinterlegte Farbe bleibt der Chip in der Standardfarbe —
              // die lokale Klasse faellt dann auf das Brand-Token zurueck.
              return (
                <AppTooltip key={departmentId || department} content={department}>
                  <AppChip
                    variant="primary"
                    appearance="outlined"
                    size="sm"
                    className="team-table__department-chip"
                    style={known?.color ? { "--team-department-color": known.color } : undefined}
                  >
                    {shorthand}
                  </AppChip>
                </AppTooltip>
              );
            })}
          </span>
        );
      },
    },
    columnVisibility.employment && {
      key: "employment",
      label: t("team.columns.employment"),
      // grow: 0 — die drei Chip-Spalten stehen auf ihrer gemessenen Mindestbreite
      // und geben allen zusaetzlichen Platz an "Abteilungen" ab. Gemessen im
      // Browser: Kopfzeile "ANSTELLUNG" mit Sortierpfeil braucht 144px, der
      // Chip 110px + 2x16px Zellabstand = 142px. Die Kopfzeile gibt das Mass.
      minWidth: 148,
      grow: 0,
      align: "center",
      render: (value) => {
        const label = value === "Perm"
          ? t("registration.full_time")
          : value === "Temp"
            ? t("registration.part_time")
            : value;
        if (!label) return "-";

        return (
          <AppChip
            variant="neutral"
            appearance="semiFilled"
            size="sm"
            className="team-table__employment-chip"
          >
            {label}
          </AppChip>
        );
      },
    },
    columnVisibility.employment_level && {
      key: "employment_level",
      label: t("team.columns.employment_level"),
      // Hier gibt die Kopfzeile das Mass vor, nicht der Inhalt: "ANSTELLUNGSGRAD"
      // braucht mit Sortierpfeil 172px, der Chip "100 %" nur 81px. Ein kuerzeres
      // Wort im Kopf waere der einzige Weg, die Spalte weiter zu verschmaelern.
      minWidth: 176,
      grow: 0,
      align: "center",
      render: (value) => {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) return "-";

        return (
          <AppChip variant="neutral" appearance="semiFilled" size="sm">
            {`${Math.round(numericValue * 100)} %`}
          </AppChip>
        );
      },
    },
    columnVisibility.access_level && {
      key: "access_level",
      label: t("team.columns.access_level"),
      // Kopfzeile "ZUGRIFFSLEVEL" braucht 147px, der Chip "Super Admin" mit
      // Symbol 141px — beides inklusive Zellabstand.
      minWidth: 152,
      grow: 0,
      align: "center",
      render: (value) => <AccessLevelChip value={value} />,
    },
    !readOnly && {
      key: "actions",
      label: "",
      width: documentsEnabled && accessLevel === "Super_Admin" ? 152 : 112,
      grow: 0,
      align: "center",
      controlKind: "action",
      sortable: false,
      render: (_value, row) => {
        const attachmentCount = Number(row.attachment_count || 0);
        return (
          <span className="team-table__actions">
            {documentsEnabled ? (
              <ActionButton
                label={t("team.attachments.open")}
                icon={<IconAttachment />}
                variant={attachmentCount > 0 ? "info" : "neutral"}
                onClick={() => onAttachmentsOpen(row)}
              />
            ) : null}
            {accessLevel === "Super_Admin" ? (
              <ActionButton
                label={t("team.password.reset_title") || "Passwort zurücksetzen"}
                icon={<PasswordIcon />}
                onClick={() => onPasswordDialogOpen(row.id)}
              />
            ) : null}
            {/*
              Kein eigener Bearbeiten-Knopf mehr: Die ganze Zeile navigiert zur
              Verwaltung (onRowClick unten). Das Chevron bleibt als sichtbarer
              Hinweis darauf — und braucht einen eigenen Klick-Handler, weil die
              Aktionszelle (controlKind "action") den Zeilenklick blockiert.
            */}
            <ActionButton
              label={t("button.edit") || "Bearbeiten"}
              icon={<IconChevronRight />}
              className="team-table__row-open"
              onClick={() => onEditUser(row.id)}
            />
          </span>
        );
      },
    },
  ].filter(Boolean);

  const minimumTableWidth = (tableColumns) =>
    tableColumns.reduce((total, column) => {
      if (typeof column.width === "number") return total + column.width;
      if (typeof column.minWidth === "number") return total + column.minWidth;
      return total + (column.controlKind ? 64 : 160);
    }, 0);

  const separateColumns = [...leadingColumns, avatarColumn, ...separateIdentityColumns, ...detailColumns];
  const mergedNameColumns = [
    ...leadingColumns,
    avatarColumn,
    nameColumn,
    ...(columnVisibility.email
      ? [{
          key: "email",
          label: t("team.columns.email"),
          minWidth: 240,
          grow: 1.5,
        }]
      : []),
    ...detailColumns,
  ];
  const mergedIdentityColumns = [
    ...leadingColumns,
    avatarColumn,
    columnVisibility.email ? nameWithEmailColumn : nameColumn,
    ...detailColumns,
  ];

  const separateWidth = minimumTableWidth(separateColumns);
  const mergedNameWidth = minimumTableWidth(mergedNameColumns);
  const usableWidth = availableWidth == null
    ? null
    : Math.max(0, availableWidth - TABLE_SCROLLBAR_ALLOWANCE);
  const columns = usableWidth == null || usableWidth >= separateWidth
    ? separateColumns
    : usableWidth >= mergedNameWidth
      ? mergedNameColumns
      : mergedIdentityColumns;

  return (
    <AppTable
      className="team-table"
      rows={users}
      columns={columns}
      totalRowCount={users.length}
      getRowId={(row) => row.id}
      selectedRowIds={selectedUserIds}
      onSelectedRowIdsChange={onSelectedUserIdsChange}
      onRowClick={readOnly ? undefined : (row) => onEditUser(row.id)}
      emptyState={t("team.no_users", { defaultValue: "Keine Mitarbeitenden vorhanden" })}
    />
  );
};

export default TeamMainGrid;
